import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthContext } from '@/lib/auth-context'
import { supabase } from '@/supabase/client'

type ProspectNotes = {
  annual_budget_cents?: number | null
  spent_cents?: number | null
  wallet_credits?: number | null
}

type Prospect = {
  id: number
  name?: string | null
  notes?: ProspectNotes | null
}

type BudgetState = {
  loading: boolean
  error: string
  annualBudget: number | null
  spent: number | null
  walletCredits: number | null
}

const INITIAL_BUDGET: BudgetState = {
  loading: false,
  error: '',
  annualBudget: null,
  spent: null,
  walletCredits: null,
}

function coerceCents(value: unknown) {
  return Number.isFinite(value) ? (value as number) : null
}

export function useLockerProspect() {
  const { user } = useAuthContext() as any
  const [prospect, setProspect] = useState<Prospect | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [budget, setBudget] = useState<BudgetState>(INITIAL_BUDGET)

  const loadProspect = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const { data, error: fetchError } = await supabase
        .from('prospects')
        .select('*')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (fetchError) throw fetchError
      setProspect(data as unknown as Prospect)
      setError('')
    } catch (loadError: any) {
      console.error('[useLockerProspect] load error', loadError)
      setProspect(null)
      setError(loadError?.message || 'Unable to load your prospect profile')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    let active = true
    const run = async () => {
      if (!active) return
      await loadProspect()
    }
    run()
    return () => {
      active = false
    }
  }, [loadProspect])

  useEffect(() => {
    let active = true
    const loadBudget = async () => {
      if (!prospect?.id) {
        if (active) setBudget(INITIAL_BUDGET)
        return
      }

      setBudget((prev) => ({ ...prev, loading: true, error: '' }))
      const { data, error: budgetError } = await supabase
        .from('prospect_budgets')
        .select('*')
        .eq('prospect_id', prospect.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!active) return

      if (budgetError) {
        console.error('[useLockerProspect] budget load error', budgetError)
        setBudget({ ...INITIAL_BUDGET, error: budgetError.message || 'Unable to load budget data' })
        return
      }

      const budgetRow = data as Record<string, unknown> | null
      const annualBudget =
        coerceCents(budgetRow?.annual_limit_cents) ?? coerceCents((budgetRow as any)?.amount_cents) ?? null
      const remaining = coerceCents((budgetRow as any)?.remaining_cents)
      const spent = coerceCents((budgetRow as any)?.spent_cents)
      const computedSpent =
        spent ?? (annualBudget != null && remaining != null ? Math.max(annualBudget - remaining, 0) : null)
      const walletCredits =
        coerceCents((budgetRow as any)?.wallet_credits) ?? coerceCents((budgetRow as any)?.credit_balance) ?? null

      setBudget({
        loading: false,
        error: '',
        annualBudget,
        spent: computedSpent,
        walletCredits,
      })
    }

    loadBudget()
    return () => {
      active = false
    }
  }, [prospect?.id])

  const mergedProspect = useMemo(() => {
    if (!prospect) return null
    const notes = { ...(prospect.notes || {}) }
    if (budget.annualBudget != null) notes.annual_budget_cents = budget.annualBudget
    if (budget.spent != null) notes.spent_cents = budget.spent
    if (budget.walletCredits != null) notes.wallet_credits = budget.walletCredits
    return { ...prospect, notes }
  }, [budget.annualBudget, budget.spent, budget.walletCredits, prospect])

  return { prospect: mergedProspect, loading, error, budget }
}
